"use client";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import "react-multi-carousel/lib/styles.css";
import "react-calendar/dist/Calendar.css";

import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import AADHeader from "./components/AADHeader.js";

import { appInsights } from "./AppInsights";
import LatLng from "./country-codes-lat-long.json";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsFullscreen from "highcharts/modules/full-screen";
import HighchartsMap from "highcharts/modules/map";
import highchartsMore from "highcharts/highcharts-more";
import SolidGauge from "highcharts/modules/solid-gauge";
import mapDataWorld from "@highcharts/map-collection/custom/world.geo.json";
import proj4 from "proj4";

import { MultiSelect } from "primereact/multiselect";
import { InputSwitch } from "primereact/inputswitch";

import CircularProgress from "@mui/material/CircularProgress";
import { Typography, Backdrop, Breadcrumbs, Link, Button } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { MdRefresh } from "react-icons/md";
import "./components/Travel_Logistic/BTCEC.css";

// Whole-number look for values >=1; keep decimals when 0<|v|<1 so small values aren't rendered as 0.
const formatMetricValue = (value) => {
  if (!Number.isFinite(value) || value === 0) return "0";
  if (Math.abs(value) >= 1) return new Intl.NumberFormat().format(Math.round(value));
  // <1: show 2 decimals; for values that round to 0.00 keep 2 significant figures so they don't read as zero.
  return Number(value.toFixed(2)) !== 0 ? value.toFixed(2) : String(Number(value.toPrecision(2)));
};

// Numeric variant of formatMetricValue for chart data points / gauge values.
const toMetricNumber = (value) => {
  if (!Number.isFinite(value)) return 0;
  if (Math.abs(value) >= 1) return Math.round(value);
  const twoDp = Number(value.toFixed(2));
  return twoDp !== 0 ? twoDp : Number(value.toPrecision(2));
};

// No Data Component
const NoDataComponent = ({ title, onConfigureClick }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "250px",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px dashed #dee2e6",
      textAlign: "center",
      padding: "15px",
    }}
  >
    <div style={{ marginBottom: "15px" }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 17H7V10H9M13 17H11V7H13M17 17H15V13H17M19.5 19.1H4.5V5H19.5M19.5 3H4.5C3.4 3 2.5 3.9 2.5 5V19.1C2.5 20.2 3.4 21.1 4.5 21.1H19.5C20.6 21.1 21.5 20.2 21.5 19.1V5C21.5 3.9 20.6 3 19.5 3Z" fill="#6c757d" />
      </svg>
    </div>
    <h6 style={{ color: "#6c757d", marginBottom: "8px", fontFamily: "Poppins", fontSize: "14px" }}>No Data Available</h6>
    <p style={{ color: "#6c757d", marginBottom: "15px", fontSize: "12px" }}>Please configure the data for visual</p>
    <Button
      variant="contained"
      onClick={onConfigureClick}
      style={{
        backgroundColor: "#407775",
        color: "white",
        textTransform: "none",
        fontFamily: "Poppins",
        borderRadius: "6px",
        padding: "6px 16px",
        fontSize: "12px",
      }}
    >
      Configure Data
    </Button>
  </div>
);

// Initialize the exporting module
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsFullscreen(Highcharts);
HighchartsMap(Highcharts);
highchartsMore(Highcharts);
SolidGauge(Highcharts);
Highcharts.setOptions({
  colors: ["#407775", "#fab430", "#c8e1e0", "#b8d9d8", "#b8d9d8", "#b8d9d8", "#b8d9d8", "#fcd283", "#fde4b4", "#fbc051", "#e09405", "#fef6e6"],
  chart: {
    style: {
      fontFamily: "Poppins, sans-serif",
    },
    backgroundColor: "#ffffff",
    spacingTop: 10,
    spacingBottom: 10,
    spacingLeft: 10,
    spacingRight: 10,
  },
  title: {
    style: {
      fontSize: "14px",
      fontWeight: "400",
      color: "#707070",
    },
    margin: 10,
  },
  legend: {
    itemStyle: {
      fontSize: "12px",
      fontWeight: "400",
      color: "#494949",
    },
    itemMarginTop: 2,
    itemMarginBottom: 2,
    margin: 5,
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    series: {
      marker: {
        radius: 3,
      },
    },
  },
});

function getFilterOptionValues(options) {
  if (!Array.isArray(options)) return [];
  return options.map((opt) => {
    if (opt && typeof opt === "object" && "value" in opt) return String(opt.value);
    return String(opt);
  });
}

function normalizeFilterSelection(selected, allOptions) {
  if (!Array.isArray(selected) || selected.length === 0) return [];
  const selectedValues = selected.map((item) => (item && typeof item === "object" && "value" in item ? String(item.value) : String(item)));
  const optionValues = getFilterOptionValues(allOptions);
  if (optionValues.length === 0) return selectedValues;
  if (selectedValues.length !== optionValues.length) return selectedValues;
  const selectedSet = new Set(selectedValues.map((value) => value.toLowerCase()));
  const allSelected = optionValues.every((option) => selectedSet.has(String(option).toLowerCase()));
  return allSelected ? [] : selectedValues;
}

function readStoredFilter(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === "") return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => (item && typeof item === "object" && "value" in item ? String(item.value) : String(item)));
  } catch {
    return [];
  }
}

export default function Home() {
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = React.useState(false);
  const [emissionbased, setEmissionBased] = React.useState(false);
  const [news, setNews] = useState(false);
  const [responseConfig, setResponseConfig] = useState({});
  const [legal, setLegal] = useState([]);
  const [business, setBusiness] = useState([]);
  const [site, setSite] = useState([]);
  const [year, setYear] = useState([]);
  const [allSupplier, setAllSupplier] = useState("");
  const [supplier, setSupplier] = useState([]);
  const [country, setCountry] = useState([]);
  const [employee, setEmployee] = useState("");
  const [scope, setScope] = useState("");
  const [yearr, setYearR] = useState("");
  const [countries, setCountries] = useState();
  const [businessunit, setBusinessUnit] = useState();
  const [activesite, setActiveSite] = useState();
  const [activeetities, setActiveEntities] = useState();
  const [activeyears, setActiveYears] = useState();
  const [checked, setChecked] = useState(true);
  const [viewMode, setViewMode] = useState("locationBased");

  if (typeof window !== "undefined") {
    window.proj4 = window.proj4 || proj4;
  }
  // Function to handle toggle change
  const handleToggleChange = (value) => {
    console.log(value);
    if (value === true) {
      setViewMode("locationBased");
      setChecked(value);
    } else {
      setViewMode("marketBased");
      setChecked(value);
    }
  };

  function truncateText(text, maxLength) {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  }


  const handleConfigureClick = () => {
    navigate("/configuration");
  };
  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false); // Close sidebar on mobile
      } else {
        setIsSidebarOpen(true); // Open sidebar on larger screens
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  var myHeaders = new Headers();
  myHeaders.append("username", username);
  var requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  };

  async function fetchFilters() {
    try {
      const countrySelected = readStoredFilter("scope2country");
      const yearSelected = readStoredFilter("scope2year");
      const legalSelected = readStoredFilter("scope2legal");
      const businessSelected = readStoredFilter("scope2business");
      const siteSelected = readStoredFilter("scope2site");
      const supplierSelected = readStoredFilter("scope2supplier");

      const country = normalizeFilterSelection(countrySelected, countries);
      const year = normalizeFilterSelection(yearSelected, activeyears);
      const legal = normalizeFilterSelection(legalSelected, activeetities);
      const business = normalizeFilterSelection(businessSelected, businessunit);
      const site = normalizeFilterSelection(siteSelected, activesite);
      const supplier = normalizeFilterSelection(supplierSelected, allSupplier);

      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}scope2-filters`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          site: site,
          supplier: supplier,
        }),
      });

      if (response.status === 200) {
        const responseJson = await response.json();

        if (responseJson.success === false || responseJson.status === 400) {
          setActiveEntities("");
          setActiveYears("");
          setBusinessUnit("");
          setCountries("");
          setActiveSite("");
          setAllSupplier("");
        } else {
          let legalOptions;
          let businessOptions;
          let countryOptions;
          let siteOptions;
          let supplierOptions;
          let yearOptions;
          // Check if any filters are selected
          const isAnyFilterSelected = year.length > 0 || legal.length > 0 || business.length > 0 || country.length > 0 || site.length > 0 || supplier.length > 0;

          // Generate dropdown options based on whether any filters are selected
          if (year.length > 0 && legal.length === 0 && business.length === 0 && country.length === 0 && site.length === 0 && supplier.length === 0) {
            yearOptions = Object.entries(responseJson.year).map(([key]) => ({
              label: key,
              value: key,
            }));
          } else {
            yearOptions = Object.entries(responseJson.year)
              .map(([key]) => ({
                label: key,
                value: key,
              }));
          }

          if (year.length === 0 && legal.length > 0 && business.length === 0 && country.length === 0 && site.length === 0 && supplier.length === 0) {
            legalOptions = Object.entries(responseJson.legalEntity).map(([key]) => ({
              label: key,
              value: key,
            }));
          } else {
            legalOptions = Object.entries(responseJson.legalEntity)
              .map(([key]) => ({
                label: key,
                value: key,
              }));
          }

          if (year.length === 0 && legal.length === 0 && business.length > 0 && country.length === 0 && site.length === 0 && supplier.length === 0) {
            businessOptions = Object.entries(responseJson.businessUnit).map(([key]) => ({
              label: key,
              value: key,
            }));
          } else {
            businessOptions = Object.entries(responseJson.businessUnit)
              .map(([key]) => ({
                label: key,
                value: key,
              }));
          }

          if (year.length === 0 && legal.length === 0 && business.length === 0 && country.length > 0 && site.length === 0 && supplier.length === 0) {
            countryOptions = Object.entries(responseJson.country).map(([key]) => ({
              label: key,
              value: key,
            }));
          } else {
            countryOptions = Object.entries(responseJson.country)
              .map(([key]) => ({
                label: key,
                value: key,
              }));
          }

          if (year.length === 0 && legal.length === 0 && business.length === 0 && country.length === 0 && site.length > 0 && supplier.length === 0) {
            siteOptions = Object.entries(responseJson.site).map(([key]) => ({
              label: key,
              value: key,
            }));
          } else {
            siteOptions = Object.entries(responseJson.site)
              .map(([key]) => ({
                label: key,
                value: key,
              }));
          }

          if (year.length === 0 && legal.length === 0 && business.length === 0 && country.length === 0 && site.length === 0 && supplier.length > 0) {
            supplierOptions = Object.entries(responseJson.supplier).map(([key]) => ({
              label: key,
              value: key,
            }));
          } else {
            supplierOptions = Object.entries(responseJson.supplier)
              .map(([key]) => ({
                label: key,
                value: key,
              }));
          }

          // Set the dropdown options
          setActiveYears(yearOptions);
          setActiveEntities(legalOptions);
          setBusinessUnit(businessOptions);
          setCountries(countryOptions);
          setActiveSite(siteOptions);
          setAllSupplier(supplierOptions);

          // Keep the user's visible selection; API already received the normalized (Select All => All) payload
          localStorage.setItem("scope2legal", JSON.stringify(legalSelected));
          setLegal(legalSelected);

          localStorage.setItem("scope2business", JSON.stringify(businessSelected));
          setBusiness(businessSelected);

          localStorage.setItem("scope2country", JSON.stringify(countrySelected));
          setCountry(countrySelected);

          localStorage.setItem("scope2site", JSON.stringify(siteSelected));
          setSite(siteSelected);

          localStorage.setItem("scope2supplier", JSON.stringify(supplierSelected));
          setSupplier(supplierSelected);

          localStorage.setItem("scope2year", JSON.stringify(yearSelected));
          setYear(yearSelected);
        }
      } else {
        setActiveEntities("");
        setActiveYears("");
        setBusinessUnit("");
        setCountries("");
        setActiveSite("");
        setAllSupplier("");
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchScope2() {
    try {
      setLoading(true);
      const companyid = localStorage.getItem("companyid");
      const country = normalizeFilterSelection(readStoredFilter("scope2country"), countries);
      const year = normalizeFilterSelection(readStoredFilter("scope2year"), activeyears);
      const legal = normalizeFilterSelection(readStoredFilter("scope2legal"), activeetities);
      const business = normalizeFilterSelection(readStoredFilter("scope2business"), businessunit);
      const site = normalizeFilterSelection(readStoredFilter("scope2site"), activesite);
      const supplier = normalizeFilterSelection(readStoredFilter("scope2supplier"), allSupplier);
      var myHeaders = new Headers();
      myHeaders.append("username", username);
      var requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}scope2`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          site: site,
          supplier: supplier,
        }),
      });
      if (response.status === 200) {
        const responseJson = await response.json();
        if (responseJson.success === false || responseJson.status === 400) {
          setLoading(false);
        } else {
          setScope(responseJson);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    appInsights.trackPageView();
    fetchFilters();
    fetchScope2();
  }, []);

  const handleLegalChange = (selectedLegal) => {
    setLegal(selectedLegal);
    localStorage.setItem("scope2legal", JSON.stringify(selectedLegal));
    fetchScope2();
    fetchFilters();
  };

  const handleBusinessChange = (selectedBusiness) => {
    setBusiness(selectedBusiness);
    localStorage.setItem("scope2business", JSON.stringify(selectedBusiness));
    fetchScope2();
    fetchFilters();
  };

  const handleCountryChange = (selectedCountry) => {
    setCountry(selectedCountry);
    localStorage.setItem("scope2country", JSON.stringify(selectedCountry));
    fetchScope2();
    fetchFilters();
  };

  const handleSiteChange = (selectedSite) => {
    setSite(selectedSite);
    localStorage.setItem("scope2site", JSON.stringify(selectedSite));
    fetchScope2();
    fetchFilters();
  };

  const handleSupplierChange = (selectedSupplier) => {
    setSupplier(selectedSupplier);
    localStorage.setItem("scope2supplier", JSON.stringify(selectedSupplier));
    fetchScope2();
    fetchFilters();
  };

  const handleYearChange = (selectedYear) => {
    setYear(selectedYear);
    localStorage.setItem("scope2year", JSON.stringify(selectedYear));
    fetchScope2();
    fetchFilters();
  };

  // Consuption by supplier start
  const consumptionBySupplier = scope && scope.consumptionBySupplier;

  const seriesData = consumptionBySupplier
    ? Object.entries(consumptionBySupplier).map(([supplierName, electricityConsumption]) => ({
        name: supplierName,
        y: Number.parseFloat(electricityConsumption),
      }))
    : [];
  const seriesData_location = consumptionBySupplier
    ? Object.entries(consumptionBySupplier).map(([supplierName, electricityConsumption]) => ({
        name: supplierName,
        y: Number.parseFloat(electricityConsumption),
      }))
    : [];
  const options_donut = {
    chart: {
      type: "pie",
      height: 300,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        innerSize: "40%",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.name}: ({point.percentage:.0f}%)",
          style: {
            fontWeight: "400",
            fontSize: "11px",
          },
        },
        showInLegend: true,
        size: "55%",
      },
    },
    legend: {
      enabled: false,
      layout: "horizontal",
      verticalAlign: "bottom",
      borderWidth: 0,
      itemStyle: {
        color: "#494949",
        fontWeight: "400",
        fontSize: "12px",
      },
      y: 15,
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      formatter: function () {
        const pointY = formatMetricValue(this.y);
        return `${this.series.name}: ${pointY} (${this.percentage.toFixed(0)}%)`;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        colorByPoint: true,
        data: seriesData,
      },
    ],
  };

  const options_donut_location = {
    chart: {
      type: "pie",
      height: 300,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        innerSize: "40%",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.name}: ({point.percentage:.0f}%)",
          style: {
            fontWeight: "400",
            fontSize: "11px",
          },
        },
        showInLegend: true,
        size: "55%",
      },
    },
    legend: {
      enabled: false,
      layout: "horizontal",
      verticalAlign: "bottom",
      borderWidth: 0,
      itemStyle: {
        color: "#494949",
        fontWeight: "400",
        fontSize: "12px",
      },
      y: 15,
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      formatter: function () {
        const pointY = formatMetricValue(this.y);
        return `${this.series.name}: ${pointY} (${this.percentage.toFixed(0)}%)`;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        colorByPoint: true,
        data: seriesData_location,
      },
    ],
  };

  // Consuption by supplier end

  const calculatePercentage = (total, value) => {
    return (value / total) * 100;
  };

  const sites = Object.keys(scope && scope.energyMixBySite);
  const categories = sites.map((site) => site);
  const nonRenewableData = sites.map((site) => scope && scope.energyMixBySite[site].nonRenewable);
  const renewableData = sites.map((site) => scope && scope.energyMixBySite[site].renewable);

  const data_emission = {
    categories: categories,
    series: [
      { name: "RE", data: renewableData },
      { name: "Non-RE", data: nonRenewableData },
    ],
  };

  // Calculate total for each category
  const totals = data_emission.categories.map((category, index) => {
    return data_emission.series.reduce((sum, series) => sum + series.data[index], 0);
  });

  // Calculate percentages based on totals
  const percentageData = data_emission.series.map((series) => ({
    name: series.name,
    data: series.data.map((value, index) => calculatePercentage(totals[index], value)),
  }));

  const options_stackedBar = {
    chart: {
      type: "bar",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: data_emission.categories,
    },
    yAxis: {
      min: 0,
      max: 100,
      title: {
        text: "",
      },
      tickPositions: [0, 25, 50, 75, 100],
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#333333",
        fontWeight: "normal",
        fontSize: "12px",
      },
      itemMarginTop: 2,
      itemMarginBottom: 2,
      padding: 10,
      backgroundColor: "white",
      borderColor: "#CCC",
      borderWidth: 0,
      shadow: false,
      y: 15,
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      headerFormat: "{point.x}<br/>",
      pointFormat: "{series.name}: {point.y:.0f}%<br/>",
    },
    plotOptions: {
      bar: {
        stacking: "percent",
        dataLabels: {
          enabled: true,
          format: "{point.y:.0f}%",
          style: {
            fontWeight: "400",
          },
        },
        reversed: true,
      },
    },
    series: percentageData,
  };

  const options_stackedBar_location = {
    chart: {
      type: "bar",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: data_emission.categories,
    },
    yAxis: {
      min: 0,
      max: 100,
      title: {
        text: "",
      },
      tickPositions: [0, 25, 50, 75, 100],
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#333333",
        fontWeight: "normal",
        fontSize: "12px",
      },
      itemMarginTop: 2,
      itemMarginBottom: 2,
      padding: 10,
      backgroundColor: "white",
      borderColor: "#CCC",
      borderWidth: 0,
      shadow: false,
      y: 15,
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      headerFormat: "{point.x}<br/>",
      pointFormat: "{series.name}: {point.y:.0f}%<br/>",
    },
    plotOptions: {
      bar: {
        stacking: "percent",
        dataLabels: {
          enabled: true,
          format: "{point.y:.0f}%",
          style: {
            fontWeight: "400",
          },
        },
      },
    },
    series: percentageData,
    colors: ["#407775", "#fab430"],
  };

  //RE share by month start
  const marketCategories = [];
  const marketColumnSeries = [];
  const marketLineSeries = [];

  scope &&
    scope.energyConsumptionAndREShareByMonth.forEach((data) => {
      marketCategories.push(data.month.substring(0, 3) + " " + data.year.toString().substring(2));

      marketColumnSeries.push(data.electricityConsumptionInMwh);
      marketLineSeries.push(data.renewableEnergyShare);
    });

  // Creating the desired format
  const Data_RESM = {
    categories: marketCategories,
    columnSeries: marketColumnSeries,
    lineSeries: marketLineSeries,
  };

  const Data_RESM_location = {
    categories: marketCategories,
    columnSeries: marketColumnSeries,
    lineSeries: marketLineSeries,
  };

  // Convert line series values to percentages
  const lineSeriesPercentage = Data_RESM.lineSeries.map((value) => (value / 150) * 100);

  const lineSeriesPercentage_location = Data_RESM_location.lineSeries.map((value) => (value / 150) * 100);

  const options_dualAxis = {
    chart: {
      type: "column",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: Data_RESM.categories,
      labels: {
        step: 1,
      },
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    yAxis: [
      {
        title: {
          text: "",
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      {
        title: {
          text: "",
        },
        opposite: true,
        labels: {
          format: "{value}%",
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        color: "#fab430",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
      line: {
        color: "#407775",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}%",
        },
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      shared: true,
      formatter: function () {
        let tooltip = "";
        this.points.forEach((point) => {
          let value = point.y;
          if (point.series.type === "line") {
            tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${formatMetricValue(value)}%<br/>`;
          } else {
            value = formatMetricValue(value);
            tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${value}<br/>`;
          }
        });
        return tooltip;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: Data_RESM.columnSeries,
        yAxis: 0,
      },
      {
        name: "RE Share",
        data: lineSeriesPercentage,
        type: "line",
        yAxis: 1,
        tooltip: {
          valueSuffix: "%",
        },
      },
    ],
  };

  const options_dualAxis_location = {
    chart: {
      type: "column",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: Data_RESM_location.categories,
      labels: {
        step: 1,
      },
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    yAxis: [
      {
        title: {
          text: "",
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      {
        title: {
          text: "",
        },
        opposite: true,
        labels: {
          format: "{value}%",
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        color: "#fab430",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
      line: {
        color: "#407775",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}%",
        },
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      shared: true,
      formatter: function () {
        let tooltip = "";
        this.points.forEach((point) => {
          let value = point.y;
          if (point.series.type === "line") {
            tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${formatMetricValue(value)}%<br/>`;
          } else {
            value = formatMetricValue(value);
            tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${value}<br/>`;
          }
        });
        return tooltip;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: Data_RESM_location.columnSeries,
        yAxis: 0,
      },
      {
        name: "RE Share",
        data: lineSeriesPercentage_location,
        type: "line",
        yAxis: 1,
        tooltip: {
          valueSuffix: "%",
        },
      },
    ],
  };

  //RE share by month end

  // emission by business unit start
  const energyConsumptionByBusinessUnit = scope && scope.emissionsAndConsumptionByBusinessUnit;
  const ecbbucategories = [];
  const ecbbuelectricityconsuption = [];
  const ecbbumarketbased = [];
  const ecbbulocationbased = [];

  if (energyConsumptionByBusinessUnit) {
    Object.entries(energyConsumptionByBusinessUnit).forEach(([name, value]) => {
      ecbbucategories.push(name);
      ecbbuelectricityconsuption.push(value.electricityConsumptionInMwh);
      ecbbumarketbased.push(value.marketBasedEmissions);
      ecbbulocationbased.push(value.locationBasedEmissions);
    });
  }

  const Data_emission_BU = {
    categories: ecbbucategories,
    columnSeries: ecbbuelectricityconsuption,
    lineSeries: ecbbumarketbased,
  };

  const Data_emission_BU_location = {
    categories: ecbbucategories,
    columnSeries: ecbbuelectricityconsuption,
    lineSeries: ecbbulocationbased,
  };

  const dualAxis_emissionBU = {
    chart: {
      type: "column",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: Data_emission_BU.categories,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    yAxis: [
      {
        title: {
          text: "Energy consumption (MWh)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      {
        title: {
          text: "Market based emissions (tCO2e)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        opposite: true,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        color: "#fab430",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
      line: {
        color: "#407775",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      shared: true,
      formatter: function () {
        let tooltip = "";
        this.points.forEach((point) => {
          let value = point.y;
          value = formatMetricValue(value);
          tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${value}<br/>`;
        });
        return tooltip;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: Data_emission_BU.columnSeries,
        yAxis: 0,
      },
      {
        name: "Market based emissions (tCO2e)",
        data: Data_emission_BU.lineSeries,
        type: "line",
        yAxis: 1,
      },
    ],
  };

  const dualAxis_emissionBU_location = {
    chart: {
      type: "column",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: Data_emission_BU_location.categories,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
      style: {
        fontSize: "11px",
        fontWeight: "400",
        color: "#494949",
      },
    },
    yAxis: [
      {
        title: {
          text: "Energy consumption (MWh)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      {
        title: {
          text: "Location based emissions (tCO2e)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        opposite: true,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        color: "#fab430",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
      line: {
        color: "#407775",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      shared: true,
      formatter: function () {
        let tooltip = "";
        this.points.forEach((point) => {
          let value = point.y;
          value = formatMetricValue(value);
          tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${value}<br/>`;
        });
        return tooltip;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: Data_emission_BU_location.columnSeries,
        yAxis: 0,
      },
      {
        name: "Location based emissions (tCO2e)",
        data: Data_emission_BU_location.lineSeries,
        type: "line",
        yAxis: 1,
      },
    ],
  };

  // emission by business unit end

  // Emission by site type start
  const emissionSiteData = scope && scope.energyBySiteType;
  const data_emissionsite = {
    categories: Object.keys(emissionSiteData),
    values: Object.values(emissionSiteData).map((emission) => emission.electricityConsumptionInMwh),
  };

  const data_emissionsite_location = {
    categories: Object.keys(emissionSiteData),
    values: Object.values(emissionSiteData).map((emission) => emission.electricityConsumptionInMwh),
  };

  //Site type data
  const optionsPieChart = {
    chart: {
      type: "pie",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#494949",
        fontWeight: "400",
        fontSize: "12px",
      },
      y: 15,
    },
    plotOptions: {
      pie: {
        innerSize: "50%",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
          style: {
            fontWeight: "400",
          },
        },
        showInLegend: true,
        size: "75%",
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: data_emissionsite.categories.map((site, index) => ({
          name: site,
          y: toMetricNumber(data_emissionsite.values[index]),
        })),
        tooltip: {
          style: {
            fontSize: "12px",
            fontWeight: "400",
            color: "#494949",
          },
          pointFormatter: function () {
            return '<span style="color:' + this.series.color + '">' + this.series.name + "</span>: " + formatMetricValue(this.y) + " (" + this.percentage.toFixed(0) + "%)<br/>";
          },
        },
      },
    ],
  };

  const optionsPieChartlocation = {
    chart: {
      type: "pie",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#494949",
        fontWeight: "400",
        fontSize: "12px",
      },
      y: 15,
    },
    plotOptions: {
      pie: {
        innerSize: "50%",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
          style: {
            fontWeight: "400",
          },
        },
        showInLegend: true,
        size: "75%",
        colors: ["#407775", "#fab430", "#000", "#f1f1f1", "#050505"],
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: data_emissionsite_location.categories.map((site, index) => ({
          name: site,
          y: toMetricNumber(data_emissionsite_location.values[index]),
        })),
        tooltip: {
          style: {
            fontSize: "12px",
            fontWeight: "400",
            color: "#494949",
          },
          pointFormatter: function () {
            return '<span style="color:' + this.series.color + '">' + this.series.name + "</span>: " + formatMetricValue(this.y) + " (" + this.percentage.toFixed(0) + "%)<br/>";
          },
        },
      },
    ],
  };

  // Emission by site type end
  // Dummy map data for Great Britain (UK)
  //Market Based

  // Prepare map data based on emissions and consumption by country
  const mapData =
    scope && scope.emissionsAndConsumptionByCountry
      ? scope.emissionsAndConsumptionByCountry
          .map((country) => {
            // Find the corresponding latitude and longitude
            const latLng = LatLng.find((item) => item.country === country.country);
            if (latLng) {
              return {
                z: Number.parseFloat(country.electricityConsumptionInMwh.toFixed(2)), // Bubble size based on electricity consumption
                keyword: country.country, // Country name
                name: country.country,
                lat: latLng.latitude, // Latitude
                lon: latLng.longitude, // Longitude
                emissions: country.marketBasedEmissions, // Emissions for color
                electricityConsumptionInMwh: country.electricityConsumptionInMwh, // Add electricity consumption
              };
            }
            return null;
          })
          .filter((data) => data !== null) // Remove null values
      : []; // Initialize as an empty array if no data

  let mapOptions;

  if (mapData.length > 0) {
    // Calculate dynamic min and max electricity consumption values for bubble size
    const minElectricity = Math.min(...mapData.map((d) => d.z));
    const maxElectricity = Math.max(...mapData.map((d) => d.z));

    // Define map options
    mapOptions = {
      chart: {
        map: "custom/world",
        height: 400,
        spacingBottom: 30,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          align: "left",
          verticalAlign: "top",
          symbolSize: 18,
          theme: {
            "stroke-width": 1,
            r: 0,
          },
        },
      },
      tooltip: {
        style: {
          fontSize: "12px",
          fontWeight: "400",
          color: "#494949",
        },
        headerFormat: "",
        formatter: function () {
          return "<b>" + this.point.keyword + "</b><br>Emission (tCO2e): " + formatMetricValue(this.point.emissions) + "<br>Electricity Consumption (MWh): " + formatMetricValue(this.point.electricityConsumptionInMwh);
        },
      },
      colorAxis: {
        min: Math.min(...mapData.map((d) => d.emissions)),
        max: Math.max(...mapData.map((d) => d.emissions)),
        stops: [
          [0, "#407775"], // Low emission
          [1, "#FAB430"], // High emission
        ],
        labels: {
          format: "{value}",
          formatter: function () {
            const value = this.value;
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + "k";
            }
            return value;
          },
        },
        margin: 0,
      },
      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        title: {
          text: "Emissions (tCO2e)",
          style: {
            fontSize: "10px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        symbolWidth: 400,
        itemStyle: {
          fontWeight: "400",
          fontSize: "10px",
          color: "#494949",
        },
        labelFormatter: () => '<span style="text-align:center;"></span>',
        margin: 0,
        padding: 0,
        itemMarginTop: 0,
        itemMarginBottom: 0,
        y: 15,
      },
      series: [
        {
          // Use the world map with no data as a basemap
          name: "Basemap",
          mapData: mapDataWorld,
          borderColor: "#A0A0A0",
          nullColor: "rgba(200, 200, 200, 0.3)",
          showInLegend: false,
        },
        {
          // Specify points using lat/lon
          type: "mapbubble",
          name: "",
          data: mapData,
          cursor: "pointer",
          showInLegend: false,
          minSize: "10%", // Adjusted for better visual differentiation
          maxSize: "40%", // Adjusted for better visual differentiation
          colorKey: "emissions", // Color based on emissions
          dataLabels: {
            enabled: true,
            format: "{point.name}",
          },
          point: {
            events: {
              click: function () {
                console.log(this.keyword);
              },
            },
          },
          marker: {
            lineColor: "#000000",
            lineWidth: 0.5,
          },
        },
      ],
    };
  } else {
    console.error("No data available for the map.");
  }

  // marketbased
  const renewableEPercentage = toMetricNumber(scope.renewableEnergyPercentage);

  const optionsSpeedo = {
    chart: {
      type: "solidgauge",
      height: null, // Auto-calculate height for responsiveness
      spacingTop: 0,
      spacingBottom: 0,
      spacingLeft: 0,
      spacingRight: 0,
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    title: null,
    pane: {
      center: ["50%", "90%"],
      size: "180%", // Reduced from 200% for better fit
      startAngle: -90,
      endAngle: 90,
      background: {
        backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || "#EEE",
        innerRadius: "60%",
        outerRadius: "100%",
        shape: "arc",
      },
    },
    tooltip: {
      enabled: false,
    },
    yAxis: {
      min: 0,
      max: 100,
      stops: [
        [0.3, "#407775"], // green
        [0.6, "#407775"], // yellow
        [0.9, "#407775"], // red
      ],
      lineWidth: 0,
      tickWidth: 0,
      minorTickInterval: null,
      tickAmount: 2,
      title: {
        y: -70,
        text: "",
      },
      labels: {
        enabled: false,
      },
    },
    plotOptions: {
      solidgauge: {
        dataLabels: {
          y: 5,
          borderWidth: 0,
          useHTML: true,
          format: '<div style="text-align:center"><span style="font-size:clamp(12px, 1.5vw, 15px)">{y}%</span></div>',
        },
      },
    },
    series: [
      {
        name: "Percentage",
        data: [renewableEPercentage],
      },
    ],
  };

  const mapDataL =
    scope && scope.emissionsAndConsumptionByCountry
      ? scope.emissionsAndConsumptionByCountry
          .map((country) => {
            // Find the corresponding latitude and longitude
            const latLng = LatLng.find((item) => item.country === country.country);
            if (latLng) {
              return {
                z: Number.parseFloat(country.electricityConsumptionInMwh.toFixed(2)), // Bubble size based on electricity consumption
                keyword: country.country, // Country name
                name: country.country,
                lat: latLng.latitude, // Latitude
                lon: latLng.longitude, // Longitude
                emissions: country.locationBasedEmissions, // Emissions for color
                electricityConsumptionInMwh: country.electricityConsumptionInMwh, // Add electricity consumption
              };
            }
            return null;
          })
          .filter((data) => data !== null) // Remove null values
      : []; // Initialize as an empty array if no data

  let mapOptionsLocation;

  if (mapDataL.length > 0) {
    // Calculate dynamic min and max electricity consumption values for bubble size
    const minElectricity = Math.min(...mapDataL.map((d) => d.z));
    const maxElectricity = Math.max(...mapDataL.map((d) => d.z));

    // Define map options
    mapOptionsLocation = {
      chart: {
        map: "custom/world",
        height: 400,
        spacingBottom: 30,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          align: "left",
          verticalAlign: "top",
          symbolSize: 18,
          theme: {
            "stroke-width": 1,
            r: 0,
          },
        },
      },
      tooltip: {
        style: {
          fontSize: "12px",
          fontWeight: "400",
          color: "#494949",
        },
        headerFormat: "",
        formatter: function () {
          return "<b>" + this.point.keyword + "</b><br>Emission (tCO2e): " + formatMetricValue(this.point.emissions) + "<br>Electricity Consumption (MWh): " + formatMetricValue(this.point.electricityConsumptionInMwh);
        },
      },
      colorAxis: {
        min: Math.min(...mapDataL.map((d) => d.emissions)),
        max: Math.max(...mapDataL.map((d) => d.emissions)),
        stops: [
          [0, "#407775"], // Low emission
          [1, "#FAB430"], // High emission
        ],
        labels: {
          format: "{value}",
          formatter: function () {
            const value = this.value;
            if (value >= 1000) {
              return value / 1000 + "k";
            }
            return value;
          },
        },
      },
      series: [
        {
          // Use the world map with no data as a basemap
          name: "Basemap",
          mapData: mapDataWorld,
          borderColor: "#A0A0A0",
          nullColor: "rgba(200, 200, 200, 0.3)",
          showInLegend: false,
        },
        {
          // Specify points using lat/lon
          type: "mapbubble",
          name: "",
          data: mapDataL,
          cursor: "pointer",
          showInLegend: false,
          minSize: "10%", // Minimum bubble size
          maxSize: "40%", // Maximum bubble size
          colorKey: "emissions", // Color based on emissions
          dataLabels: {
            enabled: true,
            format: "{point.name}",
          },
          point: {
            events: {
              click: function () {
                console.log(this.keyword);
              },
            },
          },
          marker: {
            lineColor: "#000000",
            lineWidth: 0.5,
          },
        },
      ],
      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        title: {
          text: "Emissions (tCO2e)",
          style: {
            fontSize: "10px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        symbolWidth: 400,
        itemStyle: {
          fontWeight: "400",
          fontSize: "10px",
          color: "#494949",
        },
        labelFormatter: () => '<span style="text-align:center;"></span>',
        margin: 0,
        padding: 0,
        itemMarginTop: 5,
        height: 20,
        itemMarginBottom: 0,
        y: 15,
      },
    };
  } else {
    console.error("No data available for the map.");
  }

  const activeyear = activeyears && activeyears.map((status) => status);
  console.log(activeyear);
  const activelegal = activeetities && activeetities.map((status) => status);
  const activebusiness = businessunit && businessunit.map((status) => status);
  const activesites = activesite && activesite.map((status) => status);
  const activesupplier = allSupplier && allSupplier.map((status) => status);
  const activecountry = countries && countries.map((status) => status);
  console.log(activecountry);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  const handleResetFilters = async () => {
    try {
      setLoading(true);
      // Clear all selected filters in state
      setLegal([]);
      setBusiness([]);
      setCountry([]);
      setSite([]);
      setSupplier([]);
      setYear([]);

      // Clear all selected filters in local storage
      localStorage.removeItem("scope2legal");
      localStorage.removeItem("scope2business");
      localStorage.removeItem("scope2country");
      localStorage.removeItem("scope2site");
      localStorage.removeItem("scope2supplier");
      localStorage.removeItem("scope2year");

      // Fetch filters again to reset dropdowns to show all values
      await fetchFilters();

      // Fetch scope 2 data again with no filters applied
      await fetchScope2();
    } catch (error) {
      console.error("Error resetting filters:", error);
    }
  };
  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="row">
            <div className="col-12 col-md-6">
              <div className="steps__containt-hader">
                <h3 className="title">Scope 2 Emissions</h3>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/esg-dashboard" className="link">
                      Dashboard
                    </Link>
                    <Typography color="text.primary">Scope 2 Emissions</Typography>
                  </Breadcrumbs>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6 d-flex align-items-center justify-content-md-end flex-wrap gap-2">
              <span style={{ fontSize: "13px", marginRight: "10px" }}>Market Based</span>
              <InputSwitch checked={checked} onChange={(e) => handleToggleChange(e.value)} />
              <span style={{ fontSize: "13px", marginLeft: "10px" }}>Location Based</span>
            </div>
          </div>

          {/* Reset Button */}
          <div className="row justify-content-md-end my-2">
            <div className="col-auto">
              <Button className="refresh-cta" onClick={handleResetFilters} variant="text" startIcon={<MdRefresh />}>
                Reset Filters
              </Button>
            </div>
          </div>

          <div className="row g-3">
            {[
              {
                label: "Legal entity",
                value: legal,
                setValue: setLegal,
                handleChange: handleLegalChange,
                options: activelegal,
              },
              {
                label: "Business unit",
                value: business,
                setValue: setBusiness,
                handleChange: handleBusinessChange,
                options: activebusiness,
              },
              {
                label: "Country",
                value: country,
                setValue: setCountry,
                handleChange: handleCountryChange,
                options: activecountry,
              },
              {
                label: "Site location",
                value: site,
                setValue: setSite,
                handleChange: handleSiteChange,
                options: activesites,
              },
              {
                label: "Supplier",
                value: supplier,
                setValue: setSupplier,
                handleChange: handleSupplierChange,
                options: activesupplier,
              },
              { label: "Year", value: year, setValue: setYear, handleChange: handleYearChange, options: activeyear },
            ].map(({ label, value, setValue, handleChange, options }, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-2">
                <label className="form-label">{label}</label>
                <MultiSelect
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    handleChange(e.target.value);
                  }}
                  options={options}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="All"
                  maxSelectedLabels={1}
                  className="w-100"
                  showClear
                  showSelectAll
                  selectAllLabel="Select All"
                />
              </div>
            ))}
          </div>
          {scope && (
            <div>
              {/* Emission Summary */}
              <style>{`
                /* Ensure row uses flexbox for equal heights and widths */
                .scope2-emission-cards {
                  display: flex !important;
                  flex-wrap: nowrap !important;
                  gap: 1rem !important;
                  margin-left: 0 !important;
                  margin-right: 0 !important;
                  align-items: stretch !important;
                }
                /* Make all columns stretch to same height and equal width - override Bootstrap */
                .scope2-emission-cards > [class*="col-"],
                .scope2-emission-cards > .col-12,
                .scope2-emission-cards > .col-sm-6,
                .scope2-emission-cards > .col-md-3 {
                  display: flex !important;
                  flex-direction: column !important;
                  padding-left: 0 !important;
                  padding-right: 0 !important;
                  flex: 1 1 0% !important;
                  min-width: 0 !important;
                  max-width: none !important;
                  width: auto !important;
                }
                /* Make all boxes fill their column height and width */
                .scope2-emission-cards .employee-box,
                .scope2-gauge-card .employee-box {
                  width: 100% !important;
                  height: 100% !important;
                  min-height: 140px !important;
                  padding: 18px 16px !important;
                  display: flex !important;
                  flex-direction: column !important;
                  justify-content: space-between !important;
                  box-sizing: border-box !important;
                }
                .scope2-emission-cards .employee-box-header-scope2,
                .scope2-gauge-card .employee-box-header-scope2 {
                  font-size: clamp(11px, 1.1vw, 13px) !important;
                  line-height: 1.4 !important;
                  margin-bottom: 8px !important;
                  flex-shrink: 0 !important;
                }
                .scope2-emission-cards .employee-box-content {
                  font-size: clamp(18px, 2vw, 25px) !important;
                  line-height: 1.2 !important;
                  word-break: break-word !important;
                  flex: 1 !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                }
                .scope2-gauge-card .employee-box-content {
                  flex: 1 !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  min-height: 80px !important;
                  padding: 5px 0 !important;
                }
                .scope2-gauge-card .employee-box-content > div {
                  width: 100% !important;
                  height: 100% !important;
                  min-height: 80px !important;
                  max-height: 100% !important;
                  position: relative !important;
                }
                .scope2-gauge-card .employee-box-content svg,
                .scope2-gauge-card .employee-box-content .highcharts-container {
                  max-width: 100% !important;
                  height: auto !important;
                  width: 100% !important;
                }
                .scope2-gauge-card .employee-box-content .highcharts-root {
                  width: 100% !important;
                  height: auto !important;
                }
                /* Force all four boxes in one row on desktop with equal width */
                @media (min-width: 768px) {
                  .scope2-emission-cards {
                    flex-wrap: nowrap !important;
                    align-items: stretch !important;
                  }
                  .scope2-emission-cards > [class*="col-"],
                  .scope2-emission-cards > .col-12,
                  .scope2-emission-cards > .col-sm-6,
                  .scope2-emission-cards > .col-md-3 {
                    flex: 1 1 0% !important;
                    max-width: none !important;
                    min-width: 0 !important;
                    width: auto !important;
                  }
                  .scope2-emission-cards .employee-box,
                  .scope2-gauge-card .employee-box {
                    width: 100% !important;
                    height: 100% !important;
                  }
                }
                /* On mobile, allow wrapping */
                @media (max-width: 767px) {
                  .scope2-emission-cards {
                    flex-wrap: wrap !important;
                  }
                  .scope2-emission-cards > [class*="col-"] {
                    flex: 0 0 calc(50% - 0.5rem) !important;
                    max-width: calc(50% - 0.5rem) !important;
                  }
                }
                /* Responsive adjustments for zoomed views */
                @media (max-width: 1400px) {
                  .scope2-emission-cards {
                    align-items: stretch !important;
                  }
                  .scope2-emission-cards .employee-box,
                  .scope2-gauge-card .employee-box {
                    width: 100% !important;
                    height: 100% !important;
                    padding: 15px 14px !important;
                    min-height: 130px !important;
                  }
                  .scope2-emission-cards .employee-box-content {
                    font-size: clamp(16px, 1.8vw, 22px) !important;
                  }
                }
                /* Ensure cards don't overflow on smaller screens at zoom */
                @media (max-width: 1200px) {
                  .scope2-emission-cards > [class*="col-"] {
                    margin-bottom: 0 !important;
                  }
                }
                /* Better spacing for cards at different breakpoints */
                @media (min-width: 768px) and (max-width: 991px) {
                  .scope2-emission-cards .employee-box-header-scope2,
                  .scope2-gauge-card .employee-box-header-scope2 {
                    font-size: 12px !important;
                  }
                }
              `}</style>
              <div className="row mt-4 scope2-emission-cards">
                {[
                  { title: "Scope 2 emission-location based", value: scope.locationBasedEmissions, unit: "tCO2e" },
                  { title: "Scope 2 emission-market based", value: scope.marketBasedEmissions, unit: "tCO2e" },
                  { title: "Total energy consumption", value: scope.totalElectricityConsumptionInMwh, unit: "MWh" },
                ].map((item, index) => (
                  <div key={index} className="col-12 col-sm-6 col-md-3">
                    <div className="employee-box">
                      <div className="employee-box-header-scope2">
                        {item.title} <span className="dashboard-value">({item.unit})</span>
                      </div>
                      <div className="employee-box-content">{formatMetricValue(item.value)}</div>
                    </div>
                  </div>
                ))}

                {/* Renewable Energy Mix */}
                <div className="col-12 col-md-3 scope2-gauge-card">
                  <div className="employee-box">
                    <div className="employee-box-header-scope2">Renewable energy mix</div>
                    <div className="employee-box-content">
                      <HighchartsReact highcharts={Highcharts} options={optionsSpeedo} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <style>{`
                .charts-grid {
                  display: grid !important;
                  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)) !important;
                  gap: 1rem !important;
                  margin-top: 1rem !important;
                }
                
                @media (min-width: 1200px) {
                  .charts-grid {
                    grid-template-columns: repeat(12, 1fr) !important;
                  }
                  
                  .chart-col-lg-8 {
                    grid-column: span 8 !important;
                  }
                  
                  .chart-col-lg-4 {
                    grid-column: span 4 !important;
                  }
                  
                  .chart-col-lg-6 {
                    grid-column: span 6 !important;
                  }
                }
                
                @media (min-width: 992px) and (max-width: 1199px) {
                  .charts-grid {
                    grid-template-columns: repeat(12, 1fr) !important;
                  }
                  
                  .chart-col-lg-8 {
                    grid-column: span 12 !important;
                  }
                  
                  .chart-col-lg-4 {
                    grid-column: span 6 !important;
                  }
                  
                  .chart-col-lg-6 {
                    grid-column: span 6 !important;
                  }
                }
                
                @media (max-width: 991px) {
                  .charts-grid {
                    grid-template-columns: 1fr !important;
                  }
                  
                  .chart-col-lg-8,
                  .chart-col-lg-4,
                  .chart-col-lg-6 {
                    grid-column: span 1 !important;
                  }
                }
                
                .charts {
                  height: 100% !important;
                  display: flex !important;
                  flex-direction: column !important;
                }
                
                .charts > div {
                  height: 100% !important;
                }
              `}</style>
              <div className="charts-grid">
                <div className="chart-col-lg-8">
                  <div className="charts BTCEC-main">
                    {scope && scope.locationBasedEmissions > 0 ? (
                      <div>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Global energy consumption (MWh) and emissions (tCO2e) footprint
                        </div>
                        <HighchartsReact constructorType={"mapChart"} highcharts={Highcharts} options={viewMode === "marketBased" ? mapOptions : mapOptionsLocation} />
                      </div>
                    ) : (
                      <NoDataComponent title="Global Energy Consumption Map" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
                <div className="chart-col-lg-4">
                  <div className="charts BTCEC-main">
                    {scope && scope.totalElectricityConsumptionInMwh > 0 ? (
                      <div>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Top Electricity suppliers
                        </div>
                        <HighchartsReact highcharts={Highcharts} options={viewMode === "marketBased" ? options_donut : options_donut_location} />
                      </div>
                    ) : (
                      <NoDataComponent title="Energy Consumption by Supplier" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>

                {/* Energy Consumption & Emission */}
                <div className="chart-col-lg-8">
                  <div className="charts BTCEC-main">
                    {scope && scope.totalElectricityConsumptionInMwh > 0 ? (
                      <div>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Energy consumption (MWh) and RE Share by Month
                        </div>
                        <HighchartsReact highcharts={Highcharts} options={viewMode === "marketBased" ? options_dualAxis : options_dualAxis_location} />
                      </div>
                    ) : (
                      <NoDataComponent title="Energy Consumption and RE Share" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
                <div className="chart-col-lg-4">
                  <div className="charts BTCEC-main">
                    {scope && scope.locationBasedEmissions > 0 ? (
                      <div>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Energy mix by site location
                        </div>
                        <HighchartsReact highcharts={Highcharts} options={viewMode === "marketBased" ? options_stackedBar : options_stackedBar_location} />
                      </div>
                    ) : (
                      <NoDataComponent title="Energy Mix by Site" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>

                {/* Final Emission Graphs */}
                <div className="chart-col-lg-6">
                  <div className="charts BTCEC-main">
                    {scope && scope.locationBasedEmissions > 0 ? (
                      <div>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Energy consumption (MWh) by Site Type
                        </div>
                        <HighchartsReact highcharts={Highcharts} options={viewMode === "marketBased" ? optionsPieChart : optionsPieChartlocation} />
                      </div>
                    ) : (
                      <NoDataComponent title="Energy Consumption by Site Type" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
                <div className="chart-col-lg-6">
                  <div className="charts BTCEC-main">
                    {scope && scope.locationBasedEmissions > 0 ? (
                      <div>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Energy consumption (MWh) and Emissions by BU (tCO2e)
                        </div>
                        <HighchartsReact highcharts={Highcharts} options={viewMode === "marketBased" ? dualAxis_emissionBU : dualAxis_emissionBU_location} />
                      </div>
                    ) : (
                      <NoDataComponent title="Energy Consumption and Emissions by BU" onConfigureClick={handleConfigureClick} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
